import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Inbox, ChevronRight, Receipt } from 'lucide-react';
import { getApplications, type AppRow, type ClassGroup } from '@/api/admissions';
import { Card, Empty, StatusPill, Pill, input, fmtDate } from './ui';
import { ApplicationDrawer } from './ApplicationDrawer';

const FILTERS: { key: string; label: string; status?: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'pay', label: 'Awaiting payment', status: 'payment_pending' },
  { key: 'paid', label: 'Paid', status: 'submitted,paid' },
  { key: 'exam', label: 'Exam', status: 'exam_scheduled,exam_taken' },
  { key: 'admitted', label: 'Admitted', status: 'enrolled,accepted,admitted' },
  { key: 'wait', label: 'Waitlisted', status: 'waitlisted' },
  { key: 'no', label: 'Not admitted', status: 'rejected,declined' },
];

export function ApplicationsTab({ cycleId, groups }: { cycleId: number | ''; groups: ClassGroup[] }) {
  const [filter, setFilter] = useState('all');
  const [group, setGroup] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<number | null>(null);

  const status = FILTERS.find((f) => f.key === filter)?.status;
  const appsQ = useQuery({
    queryKey: ['adm-apps', cycleId, status, group, q, page],
    queryFn: () => getApplications({ cycle_id: cycleId || undefined, status, class_group: group || undefined, q: q || undefined, page, per_page: 30 }),
    placeholderData: (prev) => prev,
  });
  const rows: AppRow[] = appsQ.data?.data ?? [];
  const meta = appsQ.data?.meta;

  const sorted = useMemo(() => rows, [rows]);

  return (
    <div className="space-y-4">
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => { setFilter(f.key); setPage(1); }}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border whitespace-nowrap ${filter === f.key ? 'bg-[#0f172a] text-white border-[#0f172a]' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}>{f.label}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="relative sm:col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search name, reference or parent phone" className={`${input} pl-9`} />
        </div>
        <select value={group} onChange={(e) => { setGroup(e.target.value); setPage(1); }} className={input}>
          <option value="">All classes</option>
          {groups.map((g) => <option key={g.key} value={g.key}>{g.name}</option>)}
        </select>
      </div>

      <Card pad={false}>
        {appsQ.isLoading ? <p className="p-6 text-center text-sm text-slate-500">Loading applications…</p>
          : sorted.length === 0 ? <Empty icon={Inbox}>No applications here yet. Share your school code so parents can apply, or add a walk-in application.</Empty>
          : (
            <>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-slate-50 text-[11px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <tr><th className="px-4 py-3">Applicant</th><th className="px-4 py-3">Class</th><th className="px-4 py-3">Fee</th><th className="px-4 py-3">Exam</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Submitted</th><th /></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sorted.map((a) => (
                      <tr key={a.id} onClick={() => setOpenId(a.id)} className="hover:bg-slate-50 cursor-pointer">
                        <td className="px-4 py-3"><p className="font-semibold text-slate-900">{a.name}</p><p className="text-xs text-slate-500">{a.reference} · {a.guardian_phone}</p></td>
                        <td className="px-4 py-3 text-slate-700">{a.admitted_class ?? a.class_applied?.name ?? '—'}</td>
                        <td className="px-4 py-3">{a.receipt_to_verify ? <Pill tone="amber"><Receipt className="w-3 h-3" />Check receipt</Pill> : a.fee_paid ? <Pill tone="green">Paid</Pill> : <Pill tone="slate">Unpaid</Pill>}</td>
                        <td className="px-4 py-3">{a.exam_percentage != null ? <span className="font-bold text-slate-900">{a.exam_percentage}%</span> : <span className="text-xs text-slate-400">{a.exam_status === 'scheduled' ? 'Booked' : '—'}</span>}</td>
                        <td className="px-4 py-3"><StatusPill status={a.status} />{a.admission_no && <p className="text-[11px] text-slate-500 mt-1">{a.admission_no}</p>}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(a.submitted_at ?? a.created_at)}</td>
                        <td className="px-2"><ChevronRight className="w-4 h-4 text-slate-300" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ul className="md:hidden divide-y divide-slate-100">
                {sorted.map((a) => (
                  <li key={a.id}>
                    <button onClick={() => setOpenId(a.id)} className="w-full text-left px-4 py-3 flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 truncate">{a.name}</p>
                        <p className="text-xs text-slate-500">{a.admitted_class ?? a.class_applied?.name ?? '—'} · {a.reference}</p>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          <StatusPill status={a.status} />
                          {a.receipt_to_verify && <Pill tone="amber">Check receipt</Pill>}
                          {a.exam_percentage != null && <Pill tone="violet">{a.exam_percentage}%</Pill>}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-300 mt-1 shrink-0" />
                    </button>
                  </li>
                ))}
              </ul>
              {meta && meta.last_page > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-600">
                  <span>{meta.total} applications</span>
                  <div className="flex gap-2">
                    <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1.5 border rounded-lg disabled:opacity-40">Previous</button>
                    <button disabled={page >= meta.last_page} onClick={() => setPage(page + 1)} className="px-3 py-1.5 border rounded-lg disabled:opacity-40">Next</button>
                  </div>
                </div>
              )}
            </>
          )}
      </Card>

      {openId && <ApplicationDrawer id={openId} groups={groups} onClose={() => setOpenId(null)} />}
    </div>
  );
}
